import { useState } from 'react';
import { generatePath, Link } from 'react-router-dom';

import { Box, Checkbox, FormControlLabel, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { makeStyles } from 'makeStyles';

import { BASE_URL } from 'common/baseUrl';
import { triggerDownload } from 'common/fileUtils';

import { LinkButton } from 'commonComponents/LinkButton';

import { useUser } from 'api/authorization';
import { API_ENDPOINTS } from 'api/endpoints';
import { useHandleError } from 'api/hooks/errorHandling';
import { downloadSystemInfoFiles } from 'api/licenses';
import { Product, useCurrentProduct } from 'api/products';
import { EuroSymbolIcon, MoreVertIcon } from 'icons';
import { ClaimIcon } from 'icons/ClaimIcon';
import { CreateIcon } from 'icons/CreateIcon';
import { ROLE_DEFINITIONS } from 'roles/definitions';
import { RoleDependentComponent } from 'roles/RoleDependentComponent';
import { ROUTES } from 'routes/routes';

const useStyles = makeStyles()(theme => ({
    actionsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    inlineBlock: {
        display: 'inline-block',
    },
    link: {
        textDecoration: 'underline',
        color: 'inherit',
        fontSize: 'inherit',
        lineHeight: 'inherit',
    },
}));

function ClaimedLicensesLink({ currentProduct }: { currentProduct: Product }) {
    const path = generatePath(API_ENDPOINTS.REPORT_CLAIMED_LICENSES);
    const params = new URLSearchParams({ format: 'xlsx', configuration__product: currentProduct.id.toString() });
    const url = new URL(path.slice(1), `${[window.location.origin, BASE_URL].join('/')}/`);
    url.search = params.toString();

    return (
        <MenuItem
            // react-router-dom Link doesn't work here when deployed under a base url
            component="a"
            href={url.href}
            download
        >
            Claimed Licenses
        </MenuItem>
    );
}

function AdditionalActions() {
    const [anchorEl, setAnchorEl] = useState(null);
    const { data: currentProduct } = useCurrentProduct();

    const handleError = useHandleError();

    if (!currentProduct) {
        return null;
    }

    const handleClick = event => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const triggerDownloadSystemInfoFiles = () => {
        handleClose();
        downloadSystemInfoFiles(currentProduct.id)
            .then(triggerDownload)
            .catch(error => handleError(error, 'Downloading system info files failed.'));
    };

    return (
        <>
            <IconButton
                aria-label="more"
                aria-controls="addactions-menu"
                aria-haspopup="true"
                onClick={handleClick}
                color="primary"
                size="large"
            >
                <MoreVertIcon />
            </IconButton>
            <Menu id="addactions-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
                <MenuItem onClick={triggerDownloadSystemInfoFiles}>Download System Info files</MenuItem>
                <ClaimedLicensesLink currentProduct={currentProduct} />
            </Menu>
        </>
    );
}

export function LicenseListFilters({
    hideExpiredLicenses,
    setHideExpiredLicenses,
    hideInternalLicenses,
    setHideInternalLicenses,
    hideInvalidLicenses,
    setHideInvalidLicenses,
    hideLicensesWithExpiredMaintenance,
    setHideLicensesWithExpiredMaintenance,
}) {
    return (
        <Box
            sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'row',
                paddingLeft: 5,
            }}
        >
            <FormControlLabel
                control={
                    <Checkbox
                        checked={hideExpiredLicenses}
                        onChange={event => setHideExpiredLicenses(event.target.checked)}
                        name="hideExpiredLicenses"
                    />
                }
                label="Hide Expired Licenses"
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={hideInternalLicenses}
                        onChange={event => setHideInternalLicenses(event.target.checked)}
                        name="hideInternalLicenses"
                    />
                }
                label="Hide Internal Licenses"
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={hideInvalidLicenses}
                        onChange={event => setHideInvalidLicenses(event.target.checked)}
                        name="hideInvalidLicenses"
                    />
                }
                label="Hide Invalid Licenses"
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={hideLicensesWithExpiredMaintenance}
                        onChange={event => setHideLicensesWithExpiredMaintenance(event.target.checked)}
                        name="hideLicensesWithExpiredMaintenance"
                    />
                }
                label="Hide Licenses with expired Maintenance"
            />
        </Box>
    );
}

export function LicenseListActions() {
    const { data: user } = useUser();
    const { classes } = useStyles();

    return (
        <Box className={classes.actionsContainer}>
            <RoleDependentComponent
                userRole={ROLE_DEFINITIONS.LicenseManager}
                component={LinkButton}
                iconClass={EuroSymbolIcon}
                route={ROUTES.LICENSE_BILLING}
                title="Billing"
                variant="outlined"
            />
            <Tooltip
                title={
                    <span>
                        The <b>Create</b> button has been relocated. Please select a customer from the{' '}
                        <Link to={ROUTES.CUSTOMER_LIST} className={classes.link}>
                            customer table
                        </Link>{' '}
                        and click <b>New</b> to create a license for that customer.
                    </span>
                }
                arrow
            >
                <Box className={classes.inlineBlock}>
                    <RoleDependentComponent
                        title="Create"
                        userRole={ROLE_DEFINITIONS.PrivilegedCustomer}
                        component={LinkButton}
                        iconClass={CreateIcon}
                        route=""
                        variant="outlined"
                        disabled
                    />
                </Box>
            </Tooltip>
            {!user?.invalidated && (
                <RoleDependentComponent
                    userRole={ROLE_DEFINITIONS.Customer}
                    component={LinkButton}
                    iconClass={ClaimIcon}
                    route={ROUTES.LICENSE_CLAIM}
                    title="Claim"
                />
            )}
            <RoleDependentComponent userRole={ROLE_DEFINITIONS.Administrator} component={AdditionalActions} />
        </Box>
    );
}
